import { useCallback, useRef, useMemo, useEffect, useReducer } from 'react';
import { FlashbarProps } from '@amzn/awsui-components-react-v3';
import useFetchOnUserActionUsingSDS, {
	OnClickCBRequestProps,
} from './useFetchOnUserActionUsingSDS';
import { flushSync } from 'react-dom';

/**
 * A specialized hook for handling server-side paginated data with continuation tokens.
 *
 * This hook encapsulates complex pagination logic including:
 * - Page caching and memory management (keeps limited pages in memory)
 * - Continuation token preservation (keeps ALL tokens for backward navigation)
 * - Loading states and error handling
 * - Bidirectional navigation between pages
 *
 * Memory Strategy:
 * - Page data: Only keeps `maxCachedPages` around current page (default: 3)
 * - Continuation tokens: Preserves ALL tokens permanently for backward navigation
 *
 * @example
 * ```typescript
 * const {
 *   currentPageData,
 *   allFetchedData,
 *   isLoading,
 *   nextPage,
 *   previousPage
 * } = usePaginatedCallback({
 *   pageSize: 10,
 *   buildRequest: (pageIndex, token) => ({
 *     path: '/api/data',
 *     queryParams: token ? { continuationToken: token } : {}
 *   }),
 *   transformResponse: (response) => ({
 *     items: response.data,
 *     continuationToken: response.nextToken
 *   })
 * });
 * ```
 */

export interface PaginatedResponse<T> {
	items?: T[];
	continuationToken?: string;
}

interface PaginationState<T> {
	currentPageIndex: number;
	pagesCount: number;
	currentPageSize: number;
	continuationTokens: Record<number, string>;
	loadedPages: Record<number, PaginatedResponse<T>>;
	fetchingPages: Set<number>;
}

type PaginationAction<T> =
	| { type: 'SET_PAGE_SIZE'; payload: number }
	| {
			type: 'SET_PAGINATION';
			payload: { currentPageIndex: number; pagesCount: number };
	  }
	| { type: 'START_FETCHING'; payload: number }
	| {
			type: 'FINISH_FETCHING';
			payload: {
				pageIndex: number;
				data: PaginatedResponse<T>;
				token?: string;
			};
	  }
	| { type: 'STOP_FETCHING'; payload: number }
	| { type: 'CLEAR_DATA' }
	| { type: 'CLEANUP_PAGES'; payload: Set<number> };

function paginationReducer<T>(
	state: PaginationState<T>,
	action: PaginationAction<T>
): PaginationState<T> {
	switch (action.type) {
		case 'SET_PAGE_SIZE':
			return {
				...state,
				currentPageSize: action.payload,
			};

		case 'SET_PAGINATION':
			return {
				...state,
				currentPageIndex: action.payload.currentPageIndex,
				pagesCount: action.payload.pagesCount,
			};

		case 'START_FETCHING':
			return {
				...state,
				fetchingPages: new Set(state.fetchingPages).add(action.payload),
			};

		case 'FINISH_FETCHING': {
			const newFetchingPages = new Set(state.fetchingPages);

			newFetchingPages.delete(action.payload.pageIndex);

			const newState = {
				...state,
				fetchingPages: newFetchingPages,
				loadedPages: {
					...state.loadedPages,
					[action.payload.pageIndex]: action.payload.data,
				},
			};

			if (action.payload.token) {
				newState.continuationTokens = {
					...state.continuationTokens,
					[action.payload.pageIndex]: action.payload.token,
				};
			}

			return newState;
		}

		case 'STOP_FETCHING': {
			const updatedFetchingPages = new Set(state.fetchingPages);

			updatedFetchingPages.delete(action.payload);

			return {
				...state,
				fetchingPages: updatedFetchingPages,
			};
		}

		case 'CLEAR_DATA':
			return {
				...state,
				continuationTokens: {},
				loadedPages: {},
				fetchingPages: new Set(),
				currentPageIndex: 1,
				pagesCount: 1,
			};

		case 'CLEANUP_PAGES': {
			const pagesToKeep = action.payload;
			const cleanedLoadedPages = { ...state.loadedPages };

			Object.keys(cleanedLoadedPages).forEach((pageKey) => {
				const pageNum = parseInt(pageKey);

				if (!pagesToKeep.has(pageNum)) {
					delete cleanedLoadedPages[pageNum];
				}
			});

			return {
				...state,
				loadedPages: cleanedLoadedPages,
			};
		}

		default:
			return state;
	}
}

export interface UsePaginatedCallbackOptions<T, R = unknown> {
	pageSize: number;
	maxCachedPages?: number;
	skipInitialFetch?: boolean;
	buildRequest: (
		pageIndex: number,
		continuationToken?: string,
		pageSize?: number
	) => OnClickCBRequestProps;
	transformResponse?: (response: R) => PaginatedResponse<T>;
	onError?: (error: string) => void;
}

export interface PaginatedCallbackResult<T> {
	currentPageData: T[];
	allFetchedData: T[];

	isLoading: boolean;
	error: string | null;
	flashBarItems: FlashbarProps.MessageDefinition[];

	currentPageIndex: number;
	pagesCount: number;
	hasNextPage: boolean;
	pageSize: number;

	goToPage: (pageIndex: number) => Promise<void>;
	nextPage: () => Promise<void>;
	previousPage: () => Promise<void>;
	refresh: () => Promise<void>;
	updatePageSize: (newSize: number) => void;
}

export const usePaginatedCallback = <T, R = unknown>(
	options: UsePaginatedCallbackOptions<T, R>
): PaginatedCallbackResult<T> => {
	const {
		pageSize,
		maxCachedPages = 5,
		skipInitialFetch = false,
		buildRequest,
		transformResponse,
		onError,
	} = options;

	const [state, dispatch] = useReducer(paginationReducer<T>, {
		currentPageIndex: 1,
		pagesCount: 1,
		currentPageSize: pageSize,
		continuationTokens: {},
		loadedPages: {},
		fetchingPages: new Set<number>(),
	});

	const {
		result: { isLoading, error, flashBarItems },
		onClickCB: fetchData,
	} = useFetchOnUserActionUsingSDS<R>();

	const cleanupOldPages = useCallback(
		(currentPage: number) => {
			const pagesToKeep = new Set<number>();

			for (
				let i = Math.max(1, currentPage - Math.floor(maxCachedPages / 2));
				i <= currentPage + Math.floor(maxCachedPages / 2);
				i++
			) {
				pagesToKeep.add(i);
			}

			// Clean up in-flight requests for pages that will be removed
			inFlightRequests.current.forEach((_, pageIndex) => {
				if (!pagesToKeep.has(pageIndex)) {
					inFlightRequests.current.delete(pageIndex);
				}
			});

			dispatch({ type: 'CLEANUP_PAGES', payload: pagesToKeep });
		},
		[maxCachedPages]
	);

	// Use a ref to track the current state for synchronous access
	const stateRef = useRef(state);

	stateRef.current = state;

	// Use a Map to track in-flight requests to prevent race conditions
	const inFlightRequests = useRef<Map<number, Promise<void>>>(new Map());

	const fetchPage = useCallback(
		async (pageIndex: number) => {
			// Check if already loaded
			if (stateRef.current.loadedPages[pageIndex]) {
				return;
			}

			// Check if already in flight - return existing promise
			if (inFlightRequests.current.has(pageIndex)) {
				return inFlightRequests.current.get(pageIndex);
			}

			// Create new request promise
			const requestPromise = (async () => {
				dispatch({ type: 'START_FETCHING', payload: pageIndex });

				try {
					const token =
						pageIndex === 1
							? ''
							: stateRef.current.continuationTokens[pageIndex - 1];
					const request = buildRequest(
						pageIndex,
						token,
						stateRef.current.currentPageSize
					);

					const response = await fetchData(request);
					const transformedResponse = transformResponse
						? transformResponse(response)
						: (response as PaginatedResponse<T>);

					dispatch({
						type: 'FINISH_FETCHING',
						payload: {
							pageIndex,
							data: transformedResponse,
							token: transformedResponse.continuationToken,
						},
					});

					cleanupOldPages(pageIndex);
				} catch (err) {
					dispatch({ type: 'STOP_FETCHING', payload: pageIndex });
					if (onError) {
						const errorMessage =
							err instanceof Error ? err.message : String(err);

						onError(errorMessage || 'Failed to fetch data');
					}
				} finally {
					// Remove from in-flight requests when done
					inFlightRequests.current.delete(pageIndex);
				}
			})();

			// Store the promise
			inFlightRequests.current.set(pageIndex, requestPromise);

			return requestPromise;
		},
		[buildRequest, transformResponse, fetchData, cleanupOldPages, onError]
	);

	const goToPage = useCallback(
		async (pageIndex: number) => {
			const { currentPageIndex, pagesCount } = stateRef.current;

			if (pageIndex === currentPageIndex) {
				return;
			}

			if (pageIndex > pagesCount) {
				dispatch({
					type: 'SET_PAGINATION',
					payload: { currentPageIndex: pageIndex, pagesCount: pageIndex },
				});
			} else {
				dispatch({
					type: 'SET_PAGINATION',
					payload: { currentPageIndex: pageIndex, pagesCount },
				});
			}

			await fetchPage(pageIndex);
		},
		[fetchPage]
	);

	const nextPage = useCallback(async () => {
		const { currentPageIndex, pagesCount } = stateRef.current;
		const nextPageIndex = currentPageIndex + 1;

		if (currentPageIndex === pagesCount) {
			dispatch({
				type: 'SET_PAGINATION',
				payload: {
					currentPageIndex: nextPageIndex,
					pagesCount: pagesCount + 1,
				},
			});
		} else {
			dispatch({
				type: 'SET_PAGINATION',
				payload: { currentPageIndex: nextPageIndex, pagesCount },
			});
		}

		await fetchPage(nextPageIndex);
	}, [fetchPage]);

	const previousPage = useCallback(async () => {
		const { currentPageIndex, pagesCount } = stateRef.current;

		if (currentPageIndex > 1) {
			const prevPageIndex = currentPageIndex - 1;

			dispatch({
				type: 'SET_PAGINATION',
				payload: { currentPageIndex: prevPageIndex, pagesCount },
			});
			await fetchPage(prevPageIndex);
		}
	}, [fetchPage]);

	const refresh = useCallback(async () => {
		// Clear in-flight requests
		inFlightRequests.current.clear();

		flushSync(() => {
			dispatch({ type: 'CLEAR_DATA' });
		});
		await fetchPage(1);
	}, [fetchPage]);

	const updatePageSize = useCallback((newSize: number) => {
		// Clear in-flight requests
		inFlightRequests.current.clear();

		dispatch({ type: 'SET_PAGE_SIZE', payload: newSize });
		dispatch({ type: 'CLEAR_DATA' });
	}, []);

	const currentPageData = useMemo(() => {
		const currentPageResponse = state.loadedPages[state.currentPageIndex];

		return currentPageResponse?.items || [];
	}, [state.loadedPages, state.currentPageIndex]);

	const allFetchedData = useMemo(() => {
		return Object.values(state.loadedPages).flatMap((page) => page.items || []);
	}, [state.loadedPages]);

	const hasNextPage = useMemo(() => {
		return !!state.continuationTokens[state.currentPageIndex];
	}, [state.continuationTokens, state.currentPageIndex]);

	useEffect(() => {
		if (!skipInitialFetch && !state.loadedPages[state.currentPageIndex]) {
			fetchPage(state.currentPageIndex);
		}
	}, [state.currentPageIndex, state.loadedPages, fetchPage, skipInitialFetch]);

	// Cleanup in-flight requests on unmount
	useEffect(() => {
		return () => {
			inFlightRequests.current.clear();
		};
	}, []);

	return {
		currentPageData,
		allFetchedData,

		isLoading,
		error,
		flashBarItems,

		currentPageIndex: state.currentPageIndex,
		pagesCount: state.pagesCount,
		hasNextPage,
		pageSize: state.currentPageSize,

		goToPage,
		nextPage,
		previousPage,
		refresh,
		updatePageSize,
	};
};
